<template>
  <div class="AiHotelCard" :data-testid="hotelId">
    <div
      v-if="Array.isArray(images) && images.length"
      class="AiHotelCard-imagesCarousel">
      <ai-carousel
        v-model="carouselStep"
        :access-id="`${hotelName}-card-carousel`"
        :items="carouselItems!"
        hide-progress-bar>
        <template #action>
          <ai-single-carousel-button
            class="AiHotelCard-imagesCarousel-nav AiHotelCard-imagesCarousel-nav--left"
            direction="left"
            @click="previous" />
          <ai-single-carousel-button
            class="AiHotelCard-imagesCarousel-nav AiHotelCard-imagesCarousel-nav--right"
            direction="right"
            @click="next" />
        </template>
      </ai-carousel>
    </div>
    <div v-else-if="image && image.srcSet" class="AiHotelCard-image">
      <ai-image
        :alt="image.alt ?? ''"
        :src="image.src"
        :src-set="image.srcSet"
        loading="lazy"
        provider="raw" />
    </div>
    <div v-else class="AiHotelCard-image AiHotelCard-imagePlaceholder">
      <ai-icon
        :size="32"
        class="AiHotelCard-imagePlaceholderIcon"
        name="photo" />
    </div>

    <div class="AiHotelCard-description">
      <ai-logo :brand="brand" size="s" />
      <nuxt-link
        :to="
          localePath({
            name: 'hotel-hotelSlug',
            params: { hotelSlug: slug },
            query: hotelQueryParams,
          })
        "
        class="AiHotelCard-link">
        <ai-typo as="h3" variant="heading-03">
          {{ hotelName }}
        </ai-typo>
      </nuxt-link>
      <ai-location
        :city="city"
        :country="country"
        :mobile-icon="false"
        class="AiHotelCard-location"
        color="stratos-900"
        variant="positive" />
      <div v-if="pictograms" class="AiHotelCard-pictograms" role="list">
        <ai-pictograms
          v-for="pictogram in pictograms"
          :key="`AiHotelCard-pictograms-${pictogram.label}`"
          :label="pictogram.label"
          :size="32"
          :url="pictogram.url"
          class="AiHotelCard-pictograms--item"
          icon-position="center"
          role="listitem"
          show-tool-tip
          variant="positive" />
      </div>
      <ai-rating
        :background="false"
        :hotel-slug="slug"
        :rating="rating ?? 0"
        :reviews="reviews ?? 0"
        :query="query"
        variant="positive" />
    </div>

    <slot name="aside" />
  </div>
</template>

<script lang="ts" setup>
import _ from 'lodash';
import { differenceInCalendarDays } from 'date-fns';

import type { AiPicture } from '~/domains/graphql';
import { serializeRemainingGuests } from '~/domains/booking';
import { formatDate } from '~/helpers';

import AiImage from '../../atoms/AiImage/AiImage.vue';
import { AiCarouselItem } from '../AiCarousel/types';

export type Pictogram = {
  label: string;
  url: string;
};

type Props = {
  city: string;
  country: string;
  brand?: string;
  hotelId: string;
  hotelName: string;
  image?: AiPicture | null;
  images?: AiPicture[] | null;
  pictograms?: Pictogram[];
  rating?: number | null;
  reviews?: number | null;
  slug: string;
  query?: Record<string, string> | null;
};

const props = withDefaults(defineProps<Props>(), {
  brand: undefined,
  image: undefined,
  images: null,
  pictograms: undefined,
  rating: 0,
  reviews: 0,
  query: null,
});

const hotelQueryParams = computed<Props['query']>(() => {
  const clone = _.clone(props.query);
  const dates = clone?.dates;
  const roomsAndGuests = clone?.roomsAndGuests;
  const [firstDate, lastDate] = dates ? JSON.parse(dates) : '';

  return {
    ...(firstDate &&
      lastDate && {
        dateIn: formatDate(firstDate, 'yyyy-MM-dd'),
        nights: Math.abs(
          differenceInCalendarDays(firstDate, lastDate),
        ).toString(),
      }),
    ...(roomsAndGuests && {
      remainingGuests: serializeRemainingGuests(JSON.parse(roomsAndGuests)),
    }),
    ..._.omit(clone, ['dates', 'roomsAndGuests']),
  };
});

const carouselStep = ref<number>(1);
const carouselItems = computed<AiCarouselItem[]>(() => {
  if (!Array.isArray(props.images) || !props.images.length) return [];

  return props.images.map(image => ({
    component: AiImage,
    key: `${props.hotelName}-carousel-cards`,
    props: image,
  }));
});

const next = () => {
  if (carouselStep.value >= carouselItems.value.length) {
    carouselStep.value = 1;
  } else {
    carouselStep.value++;
  }
};

const previous = () => {
  if (carouselStep.value <= 1) {
    carouselStep.value = carouselItems.value.length;
  } else {
    carouselStep.value--;
  }
};

const localePath = useLocalePath();
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';

.AiHotelCard {
  background: colors.$white;
  display: grid;
  padding: constants.$padding-03;
  gap: constants.$padding-03;
  grid-template-columns: 1fr;

  @media (mq.$from-large) {
    grid-template-columns: 4fr 5fr 3fr;
  }
}

.AiHotelCard-image {
  @include mixins.rem-fallback(height, 196);

  @media (mq.$from-large) {
    @include mixins.rem-fallback(height, 280);
  }
}

.AiHotelCard-imagesCarousel {
  position: relative;
  overflow: hidden;
  @include mixins.rem-fallback(height, 196);

  @media (mq.$from-large) {
    @include mixins.rem-fallback(height, 280);
  }

  :deep(.Carousel-actions) {
    margin-top: 0;
  }

  :deep(.Carousel-arrows) {
    display: none;
  }

  :deep(.AiCarousel-slide) {
    @include mixins.rem-fallback(height, 196);

    @media (mq.$from-large) {
      @include mixins.rem-fallback(height, 280);
    }
  }
}

.AiHotelCard-imagesCarousel-nav {
  position: absolute;
  top: 45%;

  &--left {
    left: constants.$inner-01;
  }

  &--right {
    right: constants.$inner-01;
  }
}

.AiHotelCard-imagePlaceholder {
  background: colors.$neutral-300;
  display: flex;
  align-items: center;
  justify-content: center;
}

.AiHotelCard-imagePlaceholderIcon {
  color: colors.$neutral-500;
}

.AiHotelCard-pictograms {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: constants.$inner-02;

  &--item {
    margin-right: constants.$margin-01;

    &:last-child {
      margin-right: 0;
    }
  }

  @media (mq.$from-large) {
    margin-bottom: 0;
  }
}

.AiHotelCard-description {
  display: flex;
  flex-direction: column;
  gap: constants.$inner-02;

  @media (mq.$from-large) {
    @include mixins.rem-fallback(max-width, 485);

    gap: constants.$margin-01;
  }
}

.AiHotelCard-link {
  text-decoration: none;
}

.AiHotelCard-location {
  margin-bottom: constants.$margin-01;

  @media (mq.$from-large) {
    margin-bottom: 0;
  }
}
</style>
